import {
    ChangeDetectionStrategy,
    Component,
    Injector,
    input,
    computed,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { IChecklistRemindTimeData } from '@fi-shared/checklist/checklist-v2';
import {
    FiChecklist2ScheduleTimeIntervalHelperService,
    IChecklistTimeIntervalViewData,
} from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule-time-interval-helper.service';
import { fiGetArrayFilledWithIndexes } from '@fi-shared/utils/fi-get-array-filled-with-indexes';
import { AppTextService } from '@fi-shared/app-text';
import { isDefined } from '@fi-shared/utils';

enum RemindTimeIntervalOptionPropertyName {
    Month = 'Month',
    Day = 'Day',
    Hour = 'Hour',
    Minute = 'Minute',
}

interface IChecklist2ScheduleRemindTimeSelectData {
    optionPropertyName: RemindTimeIntervalOptionPropertyName;
    options: number[];
    label: string;
}
@Component({
    selector: 'fi-checklist2-schedule-remind-time',
    templateUrl: './remind-time.component.html',
    styleUrls: ['./remind-time.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checklist2-schedule-remind-time' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FiChecklist2ScheduleRemindTimeComponent,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: FiChecklist2ScheduleRemindTimeComponent,
            multi: true,
        },
    ],
    standalone: false,
})
export class FiChecklist2ScheduleRemindTimeComponent extends ControlValueAccessorValidatorBase<IChecklistRemindTimeData> {
    private fiChecklist2ScheduleTimeIntervalHelperService = inject(FiChecklist2ScheduleTimeIntervalHelperService);
    private appTextService = inject(AppTextService);
    public shortestTimeInterval = input<IChecklistTimeIntervalViewData>();

    protected timeSelectData: Readonly<IChecklist2ScheduleRemindTimeSelectData[]> = [
        {
            optionPropertyName: RemindTimeIntervalOptionPropertyName.Month,
            options: fiGetArrayFilledWithIndexes(12),
            label: this.appTextService.get('checklist.schedule_month_before_closing'),
        },
        {
            optionPropertyName: RemindTimeIntervalOptionPropertyName.Day,
            options: fiGetArrayFilledWithIndexes(31),
            label: this.appTextService.get('checklist.schedule_days_before_closing'),
        },
        {
            optionPropertyName: RemindTimeIntervalOptionPropertyName.Hour,
            options: fiGetArrayFilledWithIndexes(24),
            label: this.appTextService.get('checklist.schedule_hours_before_closing'),
        },
        {
            optionPropertyName: RemindTimeIntervalOptionPropertyName.Minute,
            options: fiGetArrayFilledWithIndexes(60),
            label: this.appTextService.get('checklist.schedule_minutes_before_closing'),
        },
    ];

    protected shownCategories = computed(() => {
        const shortestTimeInterval = this.shortestTimeInterval();
        if (!isDefined(shortestTimeInterval)) {
            return { Hour: true, Minute: true };
        }
        const intervalMs: number =
            shortestTimeInterval.endDateTime.getTime() - shortestTimeInterval.startDateTime.getTime();
        const hourMs: number = 3600000;

        return {
            Month: intervalMs > hourMs * 24 * 30,
            Day: intervalMs > 24 * hourMs,
            Hour: intervalMs > hourMs,
            Minute: intervalMs <= 24 * hourMs,
        };
    });

    constructor(injector: Injector) {
        super(injector);
    }

    protected setRemindTimeByPropertyName(propertyName: RemindTimeIntervalOptionPropertyName, value: number) {
        if (this.ngModel[propertyName] !== value) {
            this.ngModel[propertyName] = value;
            this.doPropagateChanges();
        }
    }

    protected isRemindOptionDisabled(propertyName: RemindTimeIntervalOptionPropertyName, value: number): boolean {
        if (!this.shortestTimeInterval()) {
            return !(
                (propertyName === RemindTimeIntervalOptionPropertyName.Hour ||
                    propertyName === RemindTimeIntervalOptionPropertyName.Minute) &&
                value === 0
            );
        }
        return this.fiChecklist2ScheduleTimeIntervalHelperService.isRemindTimeDataFilledAndInvalid(
            this.shortestTimeInterval(),
            Object.assign({}, this.ngModel, { [propertyName]: value }),
        );
    }

    protected isReminderTimeInvalid(): boolean {
        return false;
        //return !this.fiChecklist2ScheduleTimeIntervalHelperService.isRemindTimeSet(this.remindTime);
    }

    public doValidate(): ValidationErrors {
        return {
            required: this.isReminderTimeInvalid(),
        };
    }
}
